import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import BackLink from "../components/BackLink";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CalendarClock, CheckCircle2, XCircle, Ban, Award } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const TYPE_LABEL = {
  trial: "Trial class", one_to_one: "One-to-one", family: "Family session",
  weekly_plan: "Weekly plan", monthly_plan: "Monthly plan",
};
const STATUS_CLS = {
  requested: "bg-gold-500/15 text-gold-700", accepted: "bg-emerald-500/15 text-emerald-700",
  declined: "bg-red-500/15 text-red-600", cancelled: "bg-slate-400/20 text-slate-600",
  completed: "bg-forest-700/15 text-forest-700",
};

const PAY_TEXT = (b, t) => {
  if (b.payment_status === "paid") return t("bookings.paidAmount", { amount: b.amount });
  if (b.payment_status === "pending") return t("bookings.amountDue", { amount: b.amount });
  return b.type === "trial" ? t("bookings.freeTrial") : t("bookings.priceToBeAgreed");
};

function BookingRow({ b, mine, onAction }) {
  const { t, tx } = useLanguage();
  const [link, setLink] = useState("");
  return (
    <div className="hifz-card flex flex-wrap items-center justify-between gap-3" data-testid={`booking-${b.booking_id}`}>
      <div className="min-w-0">
        <div className="font-display font-semibold text-forest-700">
          {tx(TYPE_LABEL[b.type] || b.type)} {mine ? t("bookings.with", { teacher_name: b.teacher_name }) : t("bookings.from", { student_name: b.student_name })}
        </div>
        <div className="text-xs text-muted-foreground">
          {b.preferred_time ? new Date(b.preferred_time).toLocaleString() : t("bookings.timeToBeAgreed")}
          {b.note ? t("bookings.text", { note: b.note }) : ""}
        </div>
        <div className="text-xs text-muted-foreground" data-testid={`booking-payment-${b.booking_id}`}>{PAY_TEXT(b, t)}</div>
        {b.meeting_link && b.status === "accepted" && (
          <a href={b.meeting_link} target="_blank" rel="noopener noreferrer"
            className="text-xs font-semibold text-forest-700 underline" data-testid={`booking-join-${b.booking_id}`}>
            {t("bookings.joinSession")}
          </a>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_CLS[b.status] || "bg-sand-200"}`} data-testid={`booking-status-${b.booking_id}`}>
          {b.status}
        </span>
        {!mine && b.status === "requested" && (
          <>
            <input value={link} onChange={(e) => setLink(e.target.value)} placeholder={t("bookings.joinLinkZoomMeetOptional")}
              className="rounded-xl border border-sand-300 bg-white px-2.5 py-1.5 text-xs w-full" data-testid={`booking-link-${b.booking_id}`} />
            <button onClick={() => onAction(b.booking_id, "accept", { meeting_link: link.trim() || undefined })}className="btn-primary text-xs" data-testid={`booking-accept-${b.booking_id}`}><CheckCircle2 className="h-3.5 w-3.5" /> {t("bookings.accept")}</button>
            <button onClick={() => onAction(b.booking_id, "decline")} className="btn-ghost text-xs text-red-500" data-testid={`booking-decline-${b.booking_id}`}><XCircle className="h-3.5 w-3.5" /> {t("bookings.decline")}</button>
          </>
        )}
        {!mine && b.status === "accepted" && (
          <button onClick={() => onAction(b.booking_id, "complete")} className="btn-ghost text-xs" data-testid={`booking-complete-${b.booking_id}`}><Award className="h-3.5 w-3.5" /> {t("bookings.markCompleted")}</button>
        )}
        {mine && ["requested", "accepted"].includes(b.status) && (
          <button onClick={() => onAction(b.booking_id, "cancel")} className="btn-ghost text-xs" data-testid={`booking-cancel-${b.booking_id}`}><Ban className="h-3.5 w-3.5" /> {t("halaqa.cancel")}</button>
        )}
      </div>
    </div>
  );
}

export default function Bookings() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const load = () => api.get("/bookings").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (id, action, extra = {}) => {
    try {
      await api.post(`/bookings/${id}/action`, { action, ...extra });
      const done = { accept: "bookings.doneAccepted", decline: "bookings.doneDeclined", cancel: "bookings.doneCancelled", complete: "bookings.doneCompleted" };
      toast.success(t(done[action] || "bookings.doneUpdated"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("bookings.actionFailed")); }
  };

  if (!data) return <div className="max-w-3xl mx-auto hifz-card h-64 animate-pulse-soft bg-sand-200/50" />;

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="bookings-page">
      <div>
        <BackLink to="/app/teachers" label={t("bookings.teachers")} className="btn-ghost text-xs inline-flex mb-3" testid="bookings-back" />
        <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">{t("bookings.teachersNetwork")}</div>
        <h1 className="mt-1 font-display font-semibold text-4xl text-forest-700">{t("bookings.myBookings")}</h1>
      </div>

      {data.incoming.length > 0 && (
        <section className="space-y-3" data-testid="incoming-bookings">
          <h2 className="font-display font-semibold text-xl text-forest-700">{t("bookings.requestsFromStudents")}</h2>
          {data.incoming.map((b) => <BookingRow key={b.booking_id} b={b} mine={false} onAction={act} />)}
        </section>
      )}

      <section className="space-y-3" data-testid="outgoing-bookings">
        <h2 className="font-display font-semibold text-xl text-forest-700">{t("bookings.myLessonRequests")}</h2>
        {data.outgoing.length === 0 ? (
          <div className="hifz-card border-dashed text-center p-8">
            <CalendarClock className="h-7 w-7 mx-auto text-gold-600" />
            <p className="mt-2 text-sm text-foreground/70">{t("bookings.noLessonRequestsYetFind")}</p>
            <Link to="/app/teachers" className="btn-primary text-xs mt-4 inline-flex" data-testid="browse-teachers-cta">{t("bookings.browseTeachers")}</Link>
          </div>
        ) : (
          data.outgoing.map((b) => <BookingRow key={b.booking_id} b={b} mine={true} onAction={act} />)
        )}
      </section>
    </div>
  );
}
